import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeMouseHandler,
  type OnNodeDrag,
} from '@xyflow/react';
import { canReparent, descendantIds } from '@tive/core';
import {
  choreograph,
  inStableOrder,
  prefersReducedMotion,
  rememberOrder,
  STAGE_MS,
} from '../lib/choreography';
import { colorKeyFor, swatchFor } from '../lib/colors';
import { resolveTheme } from '../lib/theme';
import { ArrowLeft, Ban, MoveRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CARD_HEIGHT, CARD_WIDTH, hitTest, layoutOrg, type Placement } from '../lib/layout';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { ColorLegend } from './ColorLegend';
import { FilterBanner } from './FilterBanner';
import { PersonNode, type PersonNodeData } from './PersonNode';
import { TrunkEdge } from './TrunkEdge';

const nodeTypes = { person: PersonNode };
const edgeTypes = { trunk: TrunkEdge };

/**
 * React Flow's canvas chrome (minimap swatches, background dots) is drawn to
 * SVG/canvas and cannot read CSS variables, so these few brand values are
 * mirrored here. Everything else in the app uses the tokens in styles.css.
 */
const BRAND = {
  movedVivid: '#f64e4e', // Tive Red
  freshVivid: '#1dc28b', // Tive Dark Green
  darkGray: '#404040', // Tive Dark Gray
  lineStrongLight: '#d4d4d4',
  dotsDark: '#2e2e2e',
  dotsLight: '#dcdcdc',
} as const;

type PersonFlowNode = Node<PersonNodeData, 'person'>;
/** Edge plus the options smoothstep and our own trunk edge read off it. */
type OrgEdge = Edge & { pathOptions?: { borderRadius?: number; offset?: number } };

/** Drag events arrive as native mouse or touch events. */
function pointerOf(event: MouseEvent | TouchEvent): { x: number; y: number } | null {
  if ('clientX' in event) return { x: event.clientX, y: event.clientY };
  const touch = event.touches[0] ?? event.changedTouches[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : null;
}

export function OrgChart() {
  const model = useAppStore((s) => s.model);
  const collapsedIds = useAppStore((s) => s.collapsedIds);
  const focusId = useAppStore((s) => s.focusId);
  const movedIds = useAppStore((s) => s.movedIds);
  const showMinimap = useAppStore((s) => s.showMinimap);
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  const colorBy = useAppStore((s) => s.colorBy);
  const squads = useAppStore((s) => s.planSquads);
  const colorOverrides = useAppStore((s) => s.colorOverrides);
  const pulseId = useAppStore((s) => s.pulseId);
  const drop = useAppStore((s) => s.drop);
  const draggingId = useAppStore((s) => s.draggingId);
  const scenarioId = useAppStore((s) => s.scenario?.id ?? null);
  const filtered = useAppStore((s) => s.filtered);
  const exporting = useAppStore((s) => s.exporting);
  const fitSignal = useAppStore((s) => s.fitSignal);

  const startDrag = useAppStore((s) => s.startDrag);
  const setDrop = useAppStore((s) => s.setDrop);
  const applyOps = useAppStore((s) => s.applyOps);
  const toggleCollapse = useAppStore((s) => s.toggleCollapse);
  const select = useAppStore((s) => s.select);
  const setFocus = useAppStore((s) => s.setFocus);

  const { fitView, screenToFlowPosition, setCenter, getZoom } = useReactFlow();

  const collapsed = useMemo(() => new Set(collapsedIds), [collapsedIds]);
  const dotColor = dark ? BRAND.dotsDark : BRAND.dotsLight;

  const layout = useMemo(() => {
    if (!model) return null;
    return layoutOrg(model, { rootId: focusId, collapsed, keep: filtered.keep });
  }, [model, focusId, collapsed, filtered.keep]);

  const positionsRef = useRef<Map<string, Placement>>(new Map());
  positionsRef.current = layout?.positions ?? new Map<string, Placement>();

  const [nodes, setNodes, onNodesChange] = useNodesState<PersonFlowNode>([]);
  const [edges, setEdges] = useEdgesState<OrgEdge>([]);

  /**
   * The nodes for one frame.
   *
   * `ids` and `at` are passed rather than read off the layout, because the
   * first frame of an expand or a collapse draws cards somewhere other than
   * where the layout finally puts them — parked behind the manager they are
   * about to come out of, or retracting into it.
   */
  const buildNodes = useCallback(
    (
      ids: readonly string[] = layout?.visibleIds ?? [],
      at: (id: string) => Placement | undefined = (id) => layout?.positions.get(id),
      parked: ReadonlySet<string> = new Set<string>(),
    ): PersonFlowNode[] => {
      if (!model || !layout) return [];
      return ids.flatMap((id) => {
        const placement = at(id);
        if (!placement) return [];
        const isBaseRoot = model.baseById.get(id)?.managerId === null;
        return [
          {
            id,
            type: 'person' as const,
            position: { x: placement.x, y: placement.y },
            data: { personId: id },
            draggable: !isBaseRoot,
            selectable: true,
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top,
            width: CARD_WIDTH,
            height: CARD_HEIGHT,
            ...(parked.has(id) ? { className: 'is-parked' } : {}),
          },
        ];
      });
    },
    [model, layout],
  );

  /*
   * The layout decides how each report is joined to its manager, because it is
   * the thing that knows whether the report was stacked into a column or given
   * a place of its own. Reading it back off the positions here would be
   * guesswork.
   */
  const buildEdges = useCallback(
    (skip: ReadonlySet<string> = new Set<string>()): OrgEdge[] => {
      if (!model || !layout) return [];
      return layout.edges
        .filter((edge) => !skip.has(edge.childId))
        .map((edge) => {
          const person = model.byId.get(edge.childId);
          const className = cn(movedIds.has(edge.childId) && 'is-moved', person?.isNew && 'is-new');

          /*
           * A dotted line is not a reporting line, so it is drawn as neither: a
           * plain dashed bezier between two cards' sides, in ink rather than in
           * any of the colours that mean something. The trunk edge would put it
           * in the bracket with the real reports, which is the one reading it
           * must never have.
           */
          if (edge.kind === 'dotted') {
            return {
              id: edge.id,
              source: edge.parentId,
              target: edge.childId,
              sourceHandle: `dotted-${edge.side === 'left' ? 'right' : 'left'}`,
              targetHandle: edge.side,
              type: 'default',
              className: 'is-dotted',
              selectable: false,
              focusable: false,
            };
          }
          // One edge type for both shapes: every report of a manager then turns
          // at the same height, which is what makes the bracket read as one thing.
          return {
            id: edge.id,
            source: edge.parentId,
            target: edge.childId,
            /*
             * Named, because an edge that does not say gets whichever source
             * handle the card happens to render first — which the dotted-line
             * handles quietly became.
             */
            sourceHandle: 'reports',
            ...(edge.kind === 'stack' ? { targetHandle: edge.side } : {}),
            type: 'trunk',
            data: { trunkX: edge.trunkX },
            className,
            selectable: false,
            focusable: false,
          };
        });
    },
    [model, layout, movedIds],
  );

  /*
   * Where every card was on the frame before this one. Expanding parks the new
   * cards behind their manager's *new* place so they fly outwards from it;
   * collapsing retracts them into its *old* one, because nothing else has moved
   * yet. Both need a memory of the previous layout, which the layout itself
   * does not keep.
   */
  const shownRef = useRef<{ ids: string[]; positions: Map<string, Placement> }>({
    ids: [],
    positions: new Map(),
  });

  /**
   * The order cards are rendered in, which is *not* the layout`s draw order.
   *
   * React reorders keyed children by moving DOM elements, and re-inserting an
   * element cancels the transition running on it. So a card whose slot changed
   * in the same commit as its position went straight to the new position
   * instead of sliding — and which cards those were depended on the direction
   * of the permutation, which is why expanding teleported the manager while its
   * peers slid, and collapsing did precisely the opposite.
   *
   * First-seen order never permutes, so nothing is ever re-inserted.
   */
  const orderRef = useRef<string[]>([]);

  useEffect(() => {
    if (!layout) return;
    const previous = shownRef.current;
    orderRef.current = rememberOrder(orderRef.current, layout.visibleIds);
    const order = (ids: readonly string[]) => inStableOrder(orderRef.current, ids);
    const settle = () => {
      shownRef.current = { ids: [...layout.visibleIds], positions: new Map(layout.positions) };
    };

    const plan = prefersReducedMotion()
      ? ({ kind: 'none' } as const)
      : choreograph(previous.ids, layout.visibleIds, (id) => model?.byId.get(id)?.managerId);

    if (plan.kind === 'none') {
      setNodes(buildNodes(order(layout.visibleIds)));
      setEdges(buildEdges());
      settle();
      return;
    }

    if (plan.kind === 'expand') {
      const arriving = new Set(plan.parked.keys());
      // Frame one: the manager slides aside, its reports invisible behind it.
      setNodes(
        buildNodes(
          order(layout.visibleIds),
          (id) => {
            const anchor = plan.parked.get(id);
            return anchor ? layout.positions.get(anchor) : layout.positions.get(id);
          },
          arriving,
        ),
      );
      setEdges(buildEdges(arriving));

      // Frame two: out they come.
      const timer = setTimeout(() => {
        setNodes(buildNodes(order(layout.visibleIds)));
        setEdges(buildEdges());
      }, STAGE_MS);
      settle();
      return () => clearTimeout(timer);
    }

    // Collapse: the reports retract into the card before anything else moves,
    // so frame one keeps every survivor exactly where it already is.
    const going = new Set(plan.parked.keys());
    setNodes(
      buildNodes(
        order(previous.ids),
        (id) => {
          const anchor = plan.parked.get(id);
          return anchor ? previous.positions.get(anchor) : previous.positions.get(id);
        },
        going,
      ),
    );

    const timer = setTimeout(() => {
      setNodes(buildNodes(order(layout.visibleIds)));
      setEdges(buildEdges());
    }, STAGE_MS);
    settle();
    return () => clearTimeout(timer);
  }, [layout, model, buildNodes, buildEdges, setNodes, setEdges]);

  // First meaningful paint: frame the chart once the layout has settled. The
  // timer restarts on every early layout change (loading a scenario expands
  // whatever it touches), so we frame the final shape rather than the first one.
  const framedRef = useRef(false);
  useEffect(() => {
    if (framedRef.current || nodes.length === 0) return;
    const timer = setTimeout(() => {
      framedRef.current = true;
      void fitView({ padding: 0.2, duration: 0, maxZoom: 1 });
    }, 260);
    return () => clearTimeout(timer);
  }, [nodes.length, fitView]);

  // Re-frame when the visible root, or the whole scenario, changes.
  const lastFocusRef = useRef<string | null>(focusId);
  useEffect(() => {
    if (lastFocusRef.current === focusId) return;
    lastFocusRef.current = focusId;
    const timer = setTimeout(() => void fitView({ padding: 0.22, duration: 420, maxZoom: 1 }), 60);
    return () => clearTimeout(timer);
  }, [focusId, fitView]);

  const lastScenarioRef = useRef<string | null>(scenarioId);
  useEffect(() => {
    if (lastScenarioRef.current === scenarioId) return;
    lastScenarioRef.current = scenarioId;
    const timer = setTimeout(() => void fitView({ padding: 0.22, duration: 420, maxZoom: 1 }), 80);
    return () => clearTimeout(timer);
  }, [scenarioId, fitView]);

  /*
   * And when the toolbar asks, which it does on "collapse back to the top".
   * That is the one act where the viewport is certainly wrong afterwards: the
   * chart that was filling the screen has just gone, leaving a row of cards in
   * a corner of a canvas sized for the whole company. Skipped on the first
   * render so it cannot fight the initial framing above.
   */
  const lastFitRef = useRef(fitSignal);
  useEffect(() => {
    if (lastFitRef.current === fitSignal) return;
    lastFitRef.current = fitSignal;
    const timer = setTimeout(() => void fitView({ padding: 0.22, duration: 420, maxZoom: 1 }), 80);
    return () => clearTimeout(timer);
  }, [fitSignal, fitView]);

  // Centre on a person when search, the change list, or a change from Claude
  // asks for it. When they have a visible manager, frame both so the new
  // reporting line is what you actually see.
  useEffect(() => {
    if (!pulseId) return;
    const placement = positionsRef.current.get(pulseId);
    if (!placement) return;

    const managerId = model?.byId.get(pulseId)?.managerId ?? null;
    const managerPlacement = managerId ? positionsRef.current.get(managerId) : undefined;
    // Frame the manager too, but only when they are close enough that doing so
    // still leaves the person on screen — a manager of a wide org can sit tens
    // of thousands of pixels away from any one report.
    const nearby =
      managerPlacement !== undefined && Math.abs(managerPlacement.x - placement.x) < CARD_WIDTH * 3;
    const target =
      nearby && managerPlacement
        ? {
            x: (placement.x + managerPlacement.x) / 2 + CARD_WIDTH / 2,
            y: (placement.y + managerPlacement.y) / 2 + CARD_HEIGHT / 2,
          }
        : { x: placement.x + CARD_WIDTH / 2, y: placement.y + CARD_HEIGHT / 2 };

    const timer = setTimeout(() => {
      void setCenter(target.x, target.y, { zoom: Math.max(getZoom(), 0.8), duration: 480 });
    }, 90);
    return () => clearTimeout(timer);
  }, [pulseId, model, setCenter, getZoom]);

  // ---------------------------------------------------------------------------
  // Drag a card onto another card to re-parent
  // ---------------------------------------------------------------------------

  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const onNodeDragStart = useCallback<OnNodeDrag<PersonFlowNode>>(
    (_event, node) => {
      if (!model) return;
      startDrag(node.id);
      // Never allow a drop on yourself, your own subtree, or your current manager.
      const skip = new Set<string>([node.id, ...descendantIds(model, node.id)]);
      const currentManager = model.byId.get(node.id)?.managerId;
      if (currentManager) skip.add(currentManager);
      setExcluded(skip);
      setDrop({ targetId: null, ok: false, reason: null });
    },
    [model, startDrag, setDrop],
  );

  const onNodeDrag = useCallback<OnNodeDrag<PersonFlowNode>>(
    (event, node) => {
      if (!model) return;
      const pointer = pointerOf(event);
      if (!pointer) return;
      const point = screenToFlowPosition(pointer);
      const targetId = hitTest(positionsRef.current, point, excluded);

      if (!targetId) {
        if (drop.targetId !== null) setDrop({ targetId: null, ok: false, reason: null });
        return;
      }
      if (drop.targetId === targetId) return;

      const check = canReparent(model, node.id, targetId);
      setDrop({ targetId, ok: check.ok, reason: check.reason ?? null });
    },
    [model, screenToFlowPosition, excluded, drop.targetId, setDrop],
  );

  const onNodeDragStop = useCallback<OnNodeDrag<PersonFlowNode>>(
    (_event, node) => {
      const { targetId, ok } = drop;
      startDrag(null);
      setDrop({ targetId: null, ok: false, reason: null });
      setExcluded(new Set());

      if (targetId && ok) {
        void applyOps([{ op: 'move', personId: node.id, managerId: targetId }]);
      }
      // Snap the card back into its computed slot (the layout is the truth).
      setNodes(buildNodes());
    },
    [drop, startDrag, setDrop, applyOps, setNodes, buildNodes],
  );

  const onNodeDoubleClick = useCallback<NodeMouseHandler>(
    (_event, node) => toggleCollapse(node.id),
    [toggleCollapse],
  );

  const dropTargetName = drop.targetId ? model?.byId.get(drop.targetId)?.name : null;
  const draggingName = draggingId ? model?.byId.get(draggingId)?.name : null;
  const focusPerson = focusId ? model?.byId.get(focusId) : null;

  return (
    <div className={cn('h-full w-full', draggingId !== null && 'orgchart-dragging')}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onNodeDoubleClick={onNodeDoubleClick}
        onPaneClick={() => select(null)}
        // A fully expanded 330-person org is ~90,000px wide, so allow a very
        // wide zoom-out — otherwise "expand everything" can never be framed.
        minZoom={0.02}
        maxZoom={1.8}
        zoomOnDoubleClick={false}
        nodesConnectable={false}
        elementsSelectable
        selectNodesOnDrag={false}
        panOnScroll
        selectionOnDrag={false}
        // Off while a PNG is being taken, or the export would have holes where
        // the off-screen cards should be.
        onlyRenderVisibleElements={nodes.length > 180 && !exporting}
        proOptions={{ hideAttribution: true }}
        colorMode={dark ? 'dark' : 'light'}
        className="bg-[var(--color-canvas)]"
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.1} color={dotColor} />
        <Controls showInteractive={false} position="bottom-left" />
        {showMinimap ? (
          <MiniMap
            pannable
            zoomable
            position="bottom-right"
            // On a phone it would cover a third of the chart, and panning by
            // dragging the canvas is easier than aiming at a 168px map.
            className="max-sm:!hidden"
            maskColor={dark ? 'rgba(20,20,20,0.75)' : 'rgba(242,242,242,0.75)'}
            nodeColor={(node) => {
              if (movedIds.has(node.id)) return BRAND.movedVivid;
              if (model?.byId.get(node.id)?.isNew) return BRAND.freshVivid;
              // The minimap is the one place the colouring is the whole
              // picture: a hundred dots grouped by colour is the point of it.
              const person = model?.byId.get(node.id);
              const key = person ? colorKeyFor(person, colorBy, squads) : null;
              if (key) return swatchFor(key, colorOverrides, colorBy);
              return dark ? BRAND.darkGray : BRAND.lineStrongLight;
            }}
            style={{ width: 168, height: 118 }}
          />
        ) : null}

        <Panel position="top-right" className="!m-3">
          <ColorLegend />
        </Panel>

        {/* Filtering hides people rather than fading them, so something has to
            say so — otherwise a chart with most of the company missing looks
            like a chart of a much smaller company. */}
        {filtered.keep ? (
          <Panel position="top-center" className="!mt-3">
            <FilterBanner />
          </Panel>
        ) : null}

        {focusPerson ? (
          <Panel position="top-left" className="!m-3">
            <button
              type="button"
              onClick={() => setFocus(null)}
              className="flex items-center gap-2 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)]/95 px-2.5 py-1.5 text-[12px] font-medium text-[var(--color-ink-2)] shadow-[var(--shadow-card)] backdrop-blur transition-colors hover:text-[var(--color-ink)]"
            >
              <ArrowLeft size={13} />
              Viewing <span className="text-[var(--color-ink)]">{focusPerson.name}</span>'s team — show whole
              company
            </button>
          </Panel>
        ) : null}

        {draggingId ? (
          <Panel position="bottom-center" className="!mb-6">
            <div
              className={cn(
                'card-in flex max-w-[520px] items-center gap-2 rounded-xl border px-3 py-2 text-[12.5px] shadow-[var(--shadow-panel)] backdrop-blur',
                drop.targetId && drop.ok
                  ? 'border-[var(--color-fresh)] bg-[var(--color-fresh-soft)]/95 text-[var(--color-fresh)]'
                  : drop.targetId
                    ? 'border-[var(--color-moved)] bg-[var(--color-moved-soft)]/95 text-[var(--color-moved)]'
                    : 'border-[var(--color-line)] bg-[var(--color-surface)]/95 text-[var(--color-ink-2)]',
              )}
            >
              {drop.targetId && drop.ok ? (
                <>
                  <MoveRight size={15} className="shrink-0" />
                  <span>
                    Release to make <strong>{draggingName}</strong> report to{' '}
                    <strong>{dropTargetName}</strong>
                  </span>
                </>
              ) : drop.targetId ? (
                <>
                  <Ban size={15} className="shrink-0" />
                  <span>{drop.reason}</span>
                </>
              ) : (
                <span>
                  Drop <strong>{draggingName}</strong> on someone to change who they report to
                </span>
              )}
            </div>
          </Panel>
        ) : null}
      </ReactFlow>
    </div>
  );
}
